import { useCallback, useEffect, useRef, useState } from 'react';
import { gamepadStore } from './store';

const SNAP_THRESHOLD = 10; // pixels from edge where snapping activates
const SNAP_DISTANCE = 0; // pixels from edge where element snaps to
const UNSNAP_THRESHOLD = 2; // pixels of movement needed to unsnap

type SnappedEdges = {
  left: boolean;
  right: boolean;
  top: boolean;
  bottom: boolean;
};

export const useDraggable = (key: keyof ReturnType<typeof gamepadStore.getLayout>) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isSnapped, setIsSnapped] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);
  const startPosRef = useRef({ x: 0, y: 0 });
  const lastPosRef = useRef({ x: 0, y: 0 });
  const snappedEdgesRef = useRef<SnappedEdges>({ left: false, right: false, top: false, bottom: false });
  const isMouseDraggingRef = useRef(false);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    const isCustomizing = gamepadStore.getIsGamepadCustomizationOpen();
    if (!isCustomizing) return;

    // Stop event propagation in customization mode
    e.stopPropagation();
    e.preventDefault();

    const touch = e.touches[0];
    startPosRef.current = {
      x: touch.clientX,
      y: touch.clientY,
    };
    lastPosRef.current = {
      x: touch.clientX,
      y: touch.clientY,
    };
    setIsDragging(true);
  }, []);

  const shouldUnsnap = (edge: keyof SnappedEdges, dragDelta: { x: number; y: number }) => {
    if (!snappedEdgesRef.current[edge]) return false;

    switch (edge) {
      case 'left':
        return dragDelta.x > UNSNAP_THRESHOLD;
      case 'right':
        return dragDelta.x < -UNSNAP_THRESHOLD;
      case 'top':
        return dragDelta.y > UNSNAP_THRESHOLD;
      case 'bottom':
        return dragDelta.y < -UNSNAP_THRESHOLD;
      default:
        return false;
    }
  };

  const checkSnapping = (rect: DOMRect, dragDelta: { x: number; y: number }): { x: number; y: number } => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    let newX = rect.left + rect.width / 2;
    let newY = rect.top + rect.height / 2;
    let shouldSnapAny = false;
    const newSnappedEdges = { ...snappedEdgesRef.current };

    // Check left edge
    if (rect.left < SNAP_THRESHOLD && !shouldUnsnap('left', dragDelta)) {
      newX = SNAP_DISTANCE + rect.width / 2;
      newSnappedEdges.left = true;
      shouldSnapAny = true;
    } else {
      newSnappedEdges.left = false;
    }

    // Check right edge
    if (rect.right > viewportWidth - SNAP_THRESHOLD && !shouldUnsnap('right', dragDelta)) {
      newX = viewportWidth - SNAP_DISTANCE - rect.width / 2;
      newSnappedEdges.right = true;
      shouldSnapAny = true;
    } else {
      newSnappedEdges.right = false;
    }

    // Check top edge
    if (rect.top < SNAP_THRESHOLD && !shouldUnsnap('top', dragDelta)) {
      newY = SNAP_DISTANCE + rect.height / 2;
      newSnappedEdges.top = true;
      shouldSnapAny = true;
    } else {
      newSnappedEdges.top = false;
    }

    // Check bottom edge
    if (rect.bottom > viewportHeight - SNAP_THRESHOLD && !shouldUnsnap('bottom', dragDelta)) {
      newY = viewportHeight - SNAP_DISTANCE - rect.height / 2;
      newSnappedEdges.bottom = true;
      shouldSnapAny = true;
    } else {
      newSnappedEdges.bottom = false;
    }

    snappedEdgesRef.current = newSnappedEdges;
    setIsSnapped(shouldSnapAny);

    return { x: newX, y: newY };
  };

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      const isCustomizing = gamepadStore.getIsGamepadCustomizationOpen();
      if (!isDragging || !isCustomizing) return;

      // Stop event propagation in customization mode
      e.stopPropagation();
      e.preventDefault();

      const touch = e.touches[0];
      const deltaX = touch.clientX - startPosRef.current.x;
      const deltaY = touch.clientY - startPosRef.current.y;

      // Calculate drag delta since last move
      const dragDelta = {
        x: touch.clientX - lastPosRef.current.x,
        y: touch.clientY - lastPosRef.current.y,
      };

      if (elementRef.current) {
        const rect = elementRef.current.getBoundingClientRect();
        const proposedRect = new DOMRect(rect.left + deltaX, rect.top + deltaY, rect.width, rect.height);

        const snappedPos = checkSnapping(proposedRect, dragDelta);
        const newPos = gamepadStore.convertCoordsToPercentage(snappedPos.x, snappedPos.y);

        gamepadStore.updateLayout({
          [key]: {
            ...gamepadStore.getLayout()[key],
            x: newPos.x,
            y: newPos.y,
          },
        });

        startPosRef.current = {
          x: touch.clientX,
          y: touch.clientY,
        };
        lastPosRef.current = {
          x: touch.clientX,
          y: touch.clientY,
        };
      }
    },
    [isDragging, key],
  );

  const handleTouchEnd = useCallback((e: TouchEvent) => {
    if (gamepadStore.getIsGamepadCustomizationOpen()) {
      e.stopPropagation();
      e.preventDefault();
    }
    setIsDragging(false);
  }, []);

  const handleMouseDown = useCallback((e: MouseEvent) => {
    const isCustomizing = gamepadStore.getIsGamepadCustomizationOpen();
    if (!isCustomizing) return;

    // Stop event propagation in customization mode
    e.stopPropagation();
    e.preventDefault();

    isMouseDraggingRef.current = true;
    startPosRef.current = {
      x: e.clientX,
      y: e.clientY,
    };
    lastPosRef.current = {
      x: e.clientX,
      y: e.clientY,
    };
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const isCustomizing = gamepadStore.getIsGamepadCustomizationOpen();
      if (!isDragging || !isCustomizing || !isMouseDraggingRef.current) return;

      // Stop event propagation in customization mode
      e.stopPropagation();
      e.preventDefault();

      const deltaX = e.clientX - startPosRef.current.x;
      const deltaY = e.clientY - startPosRef.current.y;

      // Calculate drag delta since last move
      const dragDelta = {
        x: e.clientX - lastPosRef.current.x,
        y: e.clientY - lastPosRef.current.y,
      };

      if (elementRef.current) {
        const rect = elementRef.current.getBoundingClientRect();
        const proposedRect = new DOMRect(rect.left + deltaX, rect.top + deltaY, rect.width, rect.height);

        const snappedPos = checkSnapping(proposedRect, dragDelta);
        const newPos = gamepadStore.convertCoordsToPercentage(snappedPos.x, snappedPos.y);

        gamepadStore.updateLayout({
          [key]: {
            ...gamepadStore.getLayout()[key],
            x: newPos.x,
            y: newPos.y,
          },
        });

        startPosRef.current = {
          x: e.clientX,
          y: e.clientY,
        };
        lastPosRef.current = {
          x: e.clientX,
          y: e.clientY,
        };
      }
    },
    [isDragging, key],
  );

  const handleMouseUp = useCallback((e: MouseEvent) => {
    if (gamepadStore.getIsGamepadCustomizationOpen()) {
      e.stopPropagation();
      e.preventDefault();
    }
    isMouseDraggingRef.current = false;
    setIsDragging(false);
  }, []);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // Create cleanup function inside effect to properly handle stale closures
    const touchStart = (e: TouchEvent) => handleTouchStart(e);
    const touchMove = (e: TouchEvent) => handleTouchMove(e);
    const touchEnd = (e: TouchEvent) => handleTouchEnd(e);
    const mouseDown = (e: MouseEvent) => handleMouseDown(e);
    const mouseMove = (e: MouseEvent) => handleMouseMove(e);
    const mouseUp = (e: MouseEvent) => handleMouseUp(e);

    // Touch event listeners
    element.addEventListener('touchstart', touchStart, { passive: false });
    window.addEventListener('touchmove', touchMove, { passive: false });
    window.addEventListener('touchend', touchEnd, { passive: false });

    // Mouse event listeners (for desktop debugging)
    element.addEventListener('mousedown', mouseDown);
    window.addEventListener('mousemove', mouseMove);
    window.addEventListener('mouseup', mouseUp);

    return () => {
      // Clean up all event listeners
      if (element) {
        element.removeEventListener('touchstart', touchStart);
        element.removeEventListener('mousedown', mouseDown);
      }
      window.removeEventListener('touchmove', touchMove);
      window.removeEventListener('touchend', touchEnd);
      window.removeEventListener('mousemove', mouseMove);
      window.removeEventListener('mouseup', mouseUp);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, handleMouseDown, handleMouseMove, handleMouseUp]);

  return {
    ref: elementRef,
    isDragging,
    isSnapped,
  };
};

